import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchCatalog, productPath, type CatalogProduct } from "../lib/catalog";
import { formatCurrency, formatDateTime } from "../lib/utils";
import { fetchProjects, projectPath, type ProjectCard as ProjectCardType } from "../lib/projects";

function HeroSection() {
  const [popularItems, setPopularItems] = useState<CatalogProduct[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;
    async function loadPopular() {
      try {
        const params = new URLSearchParams();
        params.set("sort", "popular");
        params.set("limit", "10");
        const res = await fetchCatalog(params);
        if (!ignore) setPopularItems(res.products);
      } catch {
        // fail silently, loading will complete
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    void loadPopular();
    return () => { ignore = true; };
  }, []);

  return (
    <section className="relative overflow-hidden bg-nv-paper px-4 pb-16 pt-16 dark:bg-[#071526] sm:px-6 lg:px-8 lg:pt-24">
      {/* Background elements compatible with light and dark mode */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(29,78,216,0.1)_0%,transparent_50%),radial-gradient(ellipse_at_top_left,rgba(34,211,238,0.1)_0%,transparent_40%)] dark:bg-[radial-gradient(ellipse_at_bottom_right,rgba(29,78,216,0.3)_0%,transparent_50%),radial-gradient(ellipse_at_top_left,rgba(34,211,238,0.15)_0%,transparent_40%)]" />

      <div className="relative z-10 mx-auto max-w-7xl">
        {/* Core Hero Text */}
        <div className="mx-auto max-w-3xl text-center">
          <h1 className="font-display text-5xl font-bold tracking-tight text-nv-ink dark:text-white sm:text-7xl">
            Embedded systems & <br className="hidden sm:block" />
            <span className="text-nv-electric dark:text-cyan-400">automation parts.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-xl text-lg text-nv-slate dark:text-slate-300">
            Source exactly the microcontrollers, sensors, and industrial automation components you need to build, scale, and maintain your engineering projects.
          </p>
          <div className="mt-8 flex items-center justify-center gap-4">
            <Link to="/shop" className="inline-flex h-14 items-center justify-center rounded-full bg-cyan-600 px-8 font-bold text-white shadow-lg transition-transform hover:scale-105 hover:bg-cyan-500 active:scale-95">
              Browse Catalog
            </Link>
          </div>
        </div>

        {/* Top 10 Product Grid integrated into Hero */}
        <div className="mt-16 lg:mt-24">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="font-display text-2xl font-bold tracking-tight text-nv-ink dark:text-white">Top 10 Trending Items</h2>
            <Link to="/shop" className="text-sm font-semibold text-nv-electric hover:underline dark:text-cyan-400">View all &rarr;</Link>
          </div>
          
          {loading ? (
            <div className="grid min-h-[200px] place-items-center rounded-3xl border border-black/5 bg-white/50 dark:border-white/10 dark:bg-white/5">
              <span className="text-sm font-medium text-nv-slate dark:text-slate-400">Loading top items...</span>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {popularItems.map((item) => (
                <Link key={item.id} to={productPath(item.slug, item.id)} className="group flex flex-col overflow-hidden rounded-xl border border-black/5 bg-white/60 shadow-sm backdrop-blur-xl transition-all hover:-translate-y-1 hover:border-blue-500/20 hover:shadow-xl hover:shadow-blue-500/5 dark:border-white/10 dark:bg-white/5 dark:hover:border-cyan-400/30">
                  <div className="aspect-[4/3] w-full overflow-hidden bg-white dark:bg-black/20">
                    {item.image ? (
                      <img alt={item.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" src={item.image} />
                    ) : (
                      <div className="grid h-full place-items-center text-xs text-nv-slate/60">No image</div>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col justify-between p-3 pb-4">
                    <p className="line-clamp-2 text-sm font-semibold leading-snug text-nv-ink dark:text-white">{item.name}</p>
                    <p className="mt-2 text-xs font-bold text-nv-electric dark:text-cyan-400">{formatCurrency(item.price)}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function FeatureBento() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
      <div className="mb-16 text-center">
        <h2 className="font-display text-3xl font-bold tracking-tight text-nv-ink dark:text-white sm:text-5xl">
          Everything you need to build
        </h2>
        <p className="mt-4 text-nv-slate dark:text-slate-400">Premium components, fast delivery, and expert sourcing for your next project.</p>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {/* Large Feature */}
        <div className="bento-card relative overflow-hidden p-8 md:col-span-2">
          <div className="relative z-10 max-w-md">
            <span className="inline-block rounded-lg bg-blue-100/80 px-3 py-1 text-xs font-bold uppercase tracking-wider text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">Massive Inventory</span>
            <h3 className="mt-4 font-display text-2xl font-bold text-nv-ink dark:text-white">Find exactly what you need</h3>
            <p className="mt-3 leading-relaxed text-nv-slate dark:text-slate-300">
              Stop waiting on backorders. We stock thousands of microcontrollers, sensors, and passive components ready to ship immediately so you can focus on engineering, not sourcing.
            </p>
          </div>
          <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-gradient-to-br from-blue-400/20 to-purple-400/10 opacity-50 filter blur-3xl" />
        </div>

        {/* Small Feature 1 */}
        <div className="bento-card overflow-hidden p-8">
          <span className="inline-block rounded-lg bg-green-100/80 px-3 py-1 text-xs font-bold uppercase tracking-wider text-green-700 dark:bg-green-900/30 dark:text-green-300">Fast Shipping</span>
          <h3 className="mt-4 font-display text-xl font-bold text-nv-ink dark:text-white">Next-day delivery</h3>
          <p className="mt-3 text-sm leading-relaxed text-nv-slate dark:text-slate-300">
            Get your parts when you need them with our expedited nationwide shipping options.
          </p>
        </div>

        {/* Small Feature 2 */}
        <div className="bento-card overflow-hidden p-8">
          <span className="inline-block rounded-lg bg-purple-100/80 px-3 py-1 text-xs font-bold uppercase tracking-wider text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">Custom Sourcing</span>
          <h3 className="mt-4 font-display text-xl font-bold text-nv-ink dark:text-white">Hard-to-find parts</h3>
          <p className="mt-3 text-sm leading-relaxed text-nv-slate dark:text-slate-300">
            Can't find a specific IC or module? Our expert team can source rare components directly from manufacturers.
          </p>
        </div>

        {/* Medium Feature */}
        <div className="bento-card relative overflow-hidden p-8 md:col-span-2">
          <div className="relative z-10 max-w-sm flex flex-col h-full justify-center">
            <div>
              <span className="inline-block rounded-lg bg-orange-100/80 px-3 py-1 text-xs font-bold uppercase tracking-wider text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">B2B & Education</span>
              <h3 className="mt-4 font-display text-2xl font-bold text-nv-ink dark:text-white">Bulk project discounts</h3>
              <p className="mt-3 leading-relaxed text-nv-slate dark:text-slate-300">
                Purchasing for a classroom, university lab, or manufacturing run? We provide tailored quotes and bulk pricing to help stretch your budget further.
              </p>
            </div>
          </div>
          <div className="absolute -bottom-20 -right-10 h-64 w-80 translate-y-12 rotate-12 bg-gradient-to-tl from-orange-400/20 to-rose-400/10 opacity-50 filter blur-3xl" />
        </div>
      </div>
    </section>
  );
}

function PopularProjectsSection() {
  const [projects, setProjects] = useState<ProjectCardType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;
    async function loadProjects() {
      try {
        const params = new URLSearchParams();
        params.set("limit", "3");
        const res = await fetchProjects(params);
        if (!ignore) setProjects(res.projects);
      } catch {
        // fail silently
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    void loadProjects();
    return () => { ignore = true; };
  }, []);

  return (
    <section className="relative overflow-hidden bg-nv-paper py-24 dark:bg-[#081223]">
      <div className="absolute right-0 top-0 h-px w-full bg-gradient-to-r from-transparent via-nv-line to-transparent" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center justify-between gap-6 sm:flex-row">
          <div>
            <h2 className="font-display text-3xl font-bold tracking-tight text-nv-ink dark:text-white">
              Popular Projects
            </h2>
            <p className="mt-2 text-nv-slate dark:text-slate-400">Real builds. Installed systems. Work that shows what Novatech can deliver.</p>
          </div>
          <Link to="/projects" className="group inline-flex items-center gap-2 font-semibold text-nv-electric dark:text-cyan-400">
            View all projects
            <span className="transition-transform group-hover:translate-x-1">→</span>
          </Link>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {loading ? (
             <div className="col-span-full grid min-h-[200px] place-items-center rounded-3xl border border-black/5 bg-white/50 dark:border-white/10 dark:bg-white/5">
                <span className="text-sm font-medium text-nv-slate dark:text-slate-400">Loading projects...</span>
             </div>
          ) : projects.length > 0 ? (
            projects.map((project) => (
              <Link key={project.id} to={projectPath(project.slug, project.id)} className="group flex flex-col overflow-hidden rounded-[24px] border border-black/5 bg-white/60 shadow-sm backdrop-blur-xl transition-all hover:-translate-y-1 hover:border-blue-500/20 hover:shadow-xl hover:shadow-blue-500/5 dark:border-white/10 dark:bg-white/5 dark:hover:border-cyan-400/30">
                <div className="aspect-[4/3] w-full overflow-hidden bg-black/5 dark:bg-black/20">
                  {project.image ? (
                    <img alt={project.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" src={project.image} />
                  ) : (
                    <div className="grid h-full place-items-center text-xs text-nv-slate/60">Project visual coming soon</div>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex items-center justify-between gap-3 text-xs">
                    <span className="font-semibold uppercase tracking-wider text-cyan-600 dark:text-cyan-400">Project #{project.id}</span>
                    <span className="text-nv-slate dark:text-slate-400">{formatDateTime(project.addedOn)}</span>
                  </div>
                  <h3 className="mt-3 text-xl font-bold leading-snug text-nv-ink dark:text-white">{project.title}</h3>
                  <p className="mt-3 flex-1 line-clamp-3 text-sm text-nv-slate dark:text-slate-300">
                    {project.summary || "No project summary available yet."}
                  </p>
                  <div className="mt-6 flex items-center justify-between">
                    <span className="font-bold text-nv-ink dark:text-white">{formatCurrency(project.cost)}</span>
                    <span className="text-sm font-semibold text-blue-600 dark:text-cyan-400 group-hover:underline">View details</span>
                  </div>
                </div>
              </Link>
            ))
          ) : (
            <div className="col-span-full grid min-h-[200px] place-items-center rounded-3xl border border-black/5 bg-white/50 dark:border-white/10 dark:bg-white/5">
               <span className="text-sm font-medium text-nv-slate dark:text-slate-400">No projects to display yet.</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function ServicesSection() {
  const services = [
    {
      title: "Component Shop",
      description: "Access high-quality automation components, Arduino modules, sensors, and more. Fast shipping and competitive pricing guaranteed.",
      linkText: "Browse Shop \u2192",
      linkUrl: "/shop",
      image: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&q=80&w=800"
    },
    {
      title: "Expert Training",
      description: "Learn automation, IoT, and hardware programming from industry experts. Perfect for both beginners and advanced learners.",
      linkText: "Join Training \u2192",
      linkUrl: "/training",
      image: "https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?auto=format&fit=crop&q=80&w=800"
    },
    {
      title: "Custom Projects",
      description: "Transform your ideas into reality with our expert development team. From concept to deployment, we're here to help.",
      linkText: "Start Project \u2192",
      linkUrl: "/projects",
      image: "https://images.unsplash.com/photo-1581092160562-40aa08e78837?auto=format&fit=crop&q=80&w=800"
    }
  ];

  return (
    <section className="bg-slate-50 px-4 py-24 sm:px-6 lg:px-8 dark:bg-[#0B1A30]">
      <div className="mx-auto max-w-7xl">
        <div className="mb-16 text-center">
          <h2 className="font-display text-4xl font-bold tracking-tight text-blue-600 dark:text-blue-400 sm:text-5xl lg:text-5xl">Our Services</h2>
          <p className="mt-4 text-lg text-slate-500 dark:text-slate-400">Discover how we can help you bring your automation ideas to life</p>
        </div>

        <div className="grid gap-8 md:grid-cols-3 lg:gap-8">
          {services.map((svc) => (
            <div key={svc.title} className="group flex flex-col overflow-hidden rounded-2xl bg-white shadow-xl shadow-black/5 ring-1 ring-black/5 transition-all hover:-translate-y-1 hover:shadow-2xl hover:shadow-blue-500/10 dark:bg-slate-800 dark:ring-white/10">
              <div className="aspect-[16/9] w-full overflow-hidden bg-slate-100 dark:bg-slate-900">
                <img src={svc.image} alt={svc.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="flex flex-1 flex-col p-8 bg-white dark:bg-[#111A2C]">
                <h3 className="text-xl font-bold text-blue-600 dark:text-blue-400">{svc.title}</h3>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{svc.description}</p>
                <div className="mt-8">
                  <Link to={svc.linkUrl} className="inline-flex items-center font-semibold text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300">
                    {svc.linkText}
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function HomePage() {
  return (
    <main>
      <HeroSection />
      <ServicesSection />
      <FeatureBento />
      <PopularProjectsSection />
    </main>
  );
}
